
"use client";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";

/* ─── Keyframe styles injected once ─── */
const HERO_STYLES = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes floatY {
    0%, 100% { transform: translateY(0px) rotate(-8deg); }
    50%       { transform: translateY(-18px) rotate(-8deg); }
  }
  @keyframes floatMain {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-14px); }
  }
  @keyframes floatBadge1 {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    50%       { transform: translateY(-10px) translateX(4px); }
  }
  @keyframes floatBadge2 {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    50%       { transform: translateY(-8px) translateX(-4px); }
  }
  @keyframes orbPulse1 {
    0%, 100% { transform: scale(1);    opacity: 0.15; }
    50%       { transform: scale(1.2); opacity: 0.28; }
  }
  @keyframes orbPulse2 {
    0%, 100% { transform: scale(1);     opacity: 0.12; }
    50%       { transform: scale(1.15); opacity: 0.22; }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes slideTag {
    from { opacity: 0; transform: translateY(12px) scale(0.9); }
    to   { opacity: 1; transform: translateY(0)    scale(1); }
  }
  @keyframes particleDrift {
    0%   { transform: translateY(0px)   translateX(0px);   opacity: 0.4; }
    100% { transform: translateY(-80px) translateX(20px);  opacity: 0;   }
  }
  @keyframes scanLine {
    0%   { top: -2px; }
    100% { top: 100%; }
  }
  @keyframes badgePop {
    0%   { opacity: 0; transform: scale(0.6) translateY(10px); }
    70%  { transform: scale(1.08) translateY(-2px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes borderGlow {
    0%, 100% { box-shadow: 0 0 0px   0px rgba(107,92,231,0);    }
    50%       { box-shadow: 0 0 28px 6px rgba(107,92,231,0.38); }
  }
  @keyframes countUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .anim-fade-up  { animation: fadeUp 0.7s ease forwards; opacity: 0; }
  .anim-fade-in  { animation: fadeIn 0.6s ease forwards; opacity: 0; }
  .hero-orb-1    { animation: orbPulse1 6s ease-in-out infinite; }
  .hero-orb-2    { animation: orbPulse2 8s ease-in-out infinite 1s; }
  .float-back    { animation: floatY    5s ease-in-out infinite; }
  .float-main    { animation: floatMain 4s ease-in-out infinite 0.5s; }
  .float-b1      { animation: floatBadge1 4.5s ease-in-out infinite; }
  .float-b2      { animation: floatBadge2 5.5s ease-in-out infinite 1s; }
  .badge-pop-1   { animation: badgePop 0.6s cubic-bezier(.34,1.56,.64,1) forwards; opacity:0; animation-delay:1.4s; }
  .badge-pop-2   { animation: badgePop 0.6s cubic-bezier(.34,1.56,.64,1) forwards; opacity:0; animation-delay:1.8s; }
  .border-glow   { animation: borderGlow 3s ease-in-out infinite; }
  .shimmer-txt {
    background: linear-gradient(90deg, #6b5ce7, #8fa3f8, #4a5fd4, #6b5ce7);
    background-size: 200% auto;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: shimmer 3s linear infinite;
  }
  .scan-line {
    position: absolute; left: 0; width: 100%; height: 2px; z-index: 20;
    background: linear-gradient(90deg, transparent, rgba(107,92,231,0.55), transparent);
    animation: scanLine 3s linear infinite;
    pointer-events: none;
  }
`;

/* ─── Live countdown ─── */
function Countdown() {
  const [t, setT] = useState({ h: 2, m: 14, s: 38 });
  useEffect(() => {
    const id = setInterval(() => {
      setT(p => {
        let { h, m, s } = p;
        s--; if (s < 0) { s = 59; m--; } if (m < 0) { m = 59; h--; } if (h < 0) { h = 23; m = 59; s = 59; }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const z = n => String(n).padStart(2, "0");
  return <span className="font-mono tabular-nums">{z(t.h)}:{z(t.m)}:{z(t.s)}</span>;
}

/* ─── Floating particles ─── */
function Particles() {
  const pts = [
    { l:"12%", t:"18%", sz:2.5, d:0.3, dur:5.2, op:0.35 },
    { l:"28%", t:"72%", sz:1.8, d:1.1, dur:6.1, op:0.25 },
    { l:"45%", t:"35%", sz:3.0, d:0.7, dur:4.8, op:0.30 },
    { l:"62%", t:"55%", sz:2.0, d:2.0, dur:5.5, op:0.20 },
    { l:"78%", t:"22%", sz:1.5, d:0.5, dur:7.0, op:0.28 },
    { l:"88%", t:"80%", sz:2.8, d:1.6, dur:4.4, op:0.22 },
    { l:"5%",  t:"60%", sz:2.2, d:3.0, dur:6.3, op:0.18 },
    { l:"55%", t:"88%", sz:1.6, d:0.9, dur:5.8, op:0.32 },
    { l:"70%", t:"10%", sz:3.2, d:2.4, dur:4.9, op:0.20 },
    { l:"33%", t:"45%", sz:1.9, d:1.8, dur:6.7, op:0.26 },
    { l:"92%", t:"40%", sz:2.4, d:0.2, dur:5.1, op:0.30 },
    { l:"20%", t:"90%", sz:1.7, d:2.8, dur:7.2, op:0.15 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
      {pts.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-[#6b5ce7]"
          style={{
            left: p.l, top: p.t,
            width: p.sz, height: p.sz,
            opacity: p.op,
            animation: `particleDrift ${p.dur}s ease-out ${p.d}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

export default function EcommerceDevelopmentPage() {
  return (
    <div className="bg-[#0a0d1f] text-white overflow-hidden">

     
  <Helmet>

    {/* PRIMARY SEO */}
    <title>
      E-Commerce Development Company | Shopify & WooCommerce Experts | SoftCoders
    </title>

    <meta
      name="description"
      content="SoftCoders is an eCommerce development company providing Shopify development, WooCommerce development, headless commerce, custom online stores, payment gateway integration, multi-vendor marketplace solutions, and scalable eCommerce websites for businesses."
    />

    <meta
      name="keywords"
      content="
      ecommerce development company,
      ecommerce website development,
      shopify development company,
      woocommerce development company,
      custom ecommerce website,
      ecommerce web development services,
      online store development,
      ecommerce website design,
      ecommerce development india,
      headless commerce development,
      next js ecommerce website,
      shopify experts,
      ecommerce developers,
      multi vendor marketplace development,
      ecommerce solutions,
      b2b ecommerce development,
      b2c ecommerce website,
      online shopping website development,
      custom shopping cart development,
      payment gateway integration,
      ecommerce platform development,
      scalable ecommerce websites,
      enterprise ecommerce solutions,
      ecommerce software company,
      ecommerce website developers,
      magento development company,
      ecommerce ui ux design,
      ecommerce seo optimization,
      online business website development,
      ecommerce app development
      "
    />

    <meta name="author" content="SoftCoders" />
    <meta name="robots" content="index, follow" />
    <meta name="language" content="English" />
    <meta name="revisit-after" content="2 days" />

    {/* CANONICAL */}
    <link
      rel="canonical"
      href="https://softcoders.in/ecommerce-development"
    />

    {/* OPEN GRAPH */}
    <meta
      property="og:title"
      content="E-Commerce Development Company | SoftCoders"
    />

    <meta
      property="og:description"
      content="Professional Shopify, WooCommerce, and custom eCommerce development services for scalable online businesses."
    />

    <meta
      property="og:url"
      content="https://softcoders.in/ecommerce-development"
    />

    <meta property="og:type" content="website" />

    <meta
      property="og:image"
      content="https://softcoders.in/og-ecommerce-development.jpg"
    />

    {/* TWITTER SEO */}
    <meta name="twitter:card" content="summary_large_image" />

    <meta
      name="twitter:title"
      content="E-Commerce Development Company | SoftCoders"
    />

    <meta
      name="twitter:description"
      content="Shopify, WooCommerce, headless commerce, and custom eCommerce website development solutions."
    />

    <meta
      name="twitter:image"
      content="https://softcoders.in/og-ecommerce-development.jpg"
    />

    {/* GEO */}
    <meta name="geo.region" content="IN" />
    <meta name="geo.country" content="India" />

    {/* SCHEMA */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        name: "SoftCoders",
        url: "https://softcoders.in",
        logo: "https://softcoders.in/logo.png",
        image: "https://softcoders.in/og-ecommerce-development.jpg",
        description:
          "SoftCoders provides Shopify development, WooCommerce development, custom eCommerce website development, and scalable online store solutions.",
        address: {
          "@type": "PostalAddress",
          addressCountry: "India"
        },
        areaServed: {
          "@type": "Country",
          name: "India"
        },
        serviceType: [
          "Shopify Development",
          "WooCommerce Development",
          "E-Commerce Website Development",
          "Headless Commerce Development",
          "Marketplace Development"
        ],
        sameAs: [
          "https://www.facebook.com/",
          "https://www.instagram.com/",
          "https://www.linkedin.com/"
        ]
      })}
    </script>

  </Helmet>

      <style>{HERO_STYLES}</style>

      {/* ════════════════ HERO ════════════════ */}
      <section className="relative min-h-screen flex items-center px-6 lg:px-20 overflow-hidden">

        {/* Diagonal bg slice */}
        <div className="absolute inset-0 z-0">
          <div
            className="absolute right-0 top-0 w-[55%] h-full bg-[#12163a]"
            style={{ clipPath: "polygon(12% 0, 100% 0, 100% 100%, 0% 100%)" }}
          />
        </div>

        {/* Animated glow orbs */}
        <div className="hero-orb-1 absolute top-20 right-[15%] w-[380px] h-[380px] rounded-full bg-[#6b5ce7]/15 blur-[100px] pointer-events-none z-0" />
        <div className="hero-orb-2 absolute bottom-20 left-[5%]  w-[280px] h-[280px] rounded-full bg-[#2d3fb5]/15 blur-[80px]  pointer-events-none z-0" />

        {/* Particles */}
        <Particles />

        <div className="relative z-10 max-w-7xl w-full grid lg:grid-cols-2 gap-20 items-center py-24">

          {/* ── LEFT ── */}
          <div>
            {/* Animated badge */}
            <div
              className="anim-fade-up inline-flex items-center gap-2 border border-[#6b5ce7]/40 bg-[#6b5ce7]/10 px-4 py-2 rounded-full text-[#b8a9f8] text-sm mb-8"
              style={{ animationDelay: "0.1s" }}
            >
              <span className="w-2 h-2 bg-[#6b5ce7] rounded-full animate-pulse" />
              Shopify · WooCommerce · Custom
            </div>

            {/* Heading with shimmer gradient */}
            <h1
              className="anim-fade-up text-5xl md:text-[68px] font-black leading-[1.0] tracking-tight"
              style={{ animationDelay: "0.25s" }}
            >
              E-Commerce
              <span
                className="block mt-1"
                style={{ WebkitTextStroke: "2px #4a5fd4", color: "transparent" }}
              >
                Development
              </span>
              <span className="block shimmer-txt mt-1">That Sells.</span>
            </h1>

            {/* Body copy */}
            <p
              className="anim-fade-up mt-8 text-[#9aa3c2] text-lg leading-8 max-w-xl"
              style={{ animationDelay: "0.42s" }}
            >
              SoftCoders builds high-converting online stores for every scale —
              from startup storefronts to enterprise marketplaces. Fast, secure,
              and built to grow your revenue.
            </p>

            {/* Pill tags — staggered slide-up */}
            <div className="flex flex-wrap gap-3 mt-8">
              {["Shopify", "WooCommerce", "Next.js", "Magento", "Headless"].map((t, i) => (
                <span
                  key={i}
                  className="px-4 py-1.5 rounded-full text-sm border border-[#4a5fd4]/40 text-[#8fa3f8] bg-[#1a2472]/20"
                  style={{
                    animation: "slideTag 0.5s ease forwards",
                    animationDelay: `${0.58 + i * 0.09}s`,
                    opacity: 0,
                  }}
                >
                  {t}
                </span>
              ))}
            </div>

            {/* CTA buttons */}
            <div
              className="anim-fade-up flex flex-wrap gap-4 mt-10"
              style={{ animationDelay: "0.95s" }}
            >
              <a
    href="https://wa.me/919254147455?text=Hello%20SoftCoders%2C%20I%20want%20to%20discuss%20my%20project."
    target="_blank"
    rel="noopener noreferrer"
 className="relative px-8 py-4 rounded-2xl bg-gradient-to-r from-[#6b5ce7] to-[#2d3fb5] font-semibold text-lg hover:scale-105 transition duration-300 shadow-2xl shadow-[#6b5ce7]/30 overflow-hidden group">
                <span className="relative z-10">Start Your Store</span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#8fa3f8]/30 to-transparent opacity-0 group-hover:opacity-100 transition duration-300" />
              </a>
              <a
    href="/portfolio" className="px-8 py-4 rounded-2xl border border-white/20 hover:bg-white hover:text-black transition duration-300 text-lg">
                See Portfolio
              </a>
            </div>

            {/* Stats — staggered count-up entrance */}
            <div
              className="anim-fade-up flex gap-10 mt-14 border-t border-white/10 pt-10"
              style={{ animationDelay: "1.1s" }}
            >
              {[["500+", "Stores Launched"], ["$2B+", "Client Revenue"], ["99.9%", "Uptime SLA"]].map(([v, l], i) => (
                <div
                  key={i}
                  style={{
                    animation: "countUp 0.55s ease forwards",
                    animationDelay: `${1.15 + i * 0.13}s`,
                    opacity: 0,
                  }}
                >
                  <div className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#6b5ce7] to-[#8fa3f8]">{v}</div>
                  <div className="text-[#9aa3c2] text-sm mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT — floating mockup stack ── */}
          <div
            className="anim-fade-in relative flex justify-center items-center h-[600px]"
            style={{ animationDelay: "0.3s" }}
          >
            {/* Back phone — floats independently */}
            <div className="float-back absolute left-4 top-10 w-[220px] h-[440px] rounded-[36px] bg-[#1a2472]/60 border border-[#4a5fd4]/30 backdrop-blur-xl shadow-2xl overflow-hidden">
              <div className="h-6 bg-black/40 flex items-center justify-center mt-3 mx-3 rounded-full">
                <div className="w-16 h-1.5 bg-white/20 rounded-full" />
              </div>
              <div className="p-4 mt-3 space-y-3">
                <div className="h-36 rounded-2xl bg-gradient-to-br from-[#2d3fb5]/40 to-[#6b5ce7]/30 border border-[#4a5fd4]/20 flex items-center justify-center text-4xl">
                  🛍️
                </div>
                <div className="h-3 bg-white/10 rounded-full w-3/4" />
                <div className="h-3 bg-white/10 rounded-full w-1/2" />
                <div className="h-8 rounded-xl bg-gradient-to-r from-[#2d3fb5]/40 to-[#6b5ce7]/30 border border-[#4a5fd4]/20 mt-2" />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="h-14 rounded-xl bg-[#6b5ce7]/20 border border-[#6b5ce7]/20 flex items-center justify-center text-xl">👟</div>
                  <div className="h-14 rounded-xl bg-[#2d3fb5]/20 border border-[#2d3fb5]/20 flex items-center justify-center text-xl">👜</div>
                </div>
              </div>
            </div>

            {/* Front store mockup — glows + floats + scan line */}
            <div className="float-main border-glow relative w-[260px] h-[510px] rounded-[36px] bg-[#12163a] border border-[#4a5fd4]/50 shadow-2xl shadow-[#6b5ce7]/20 overflow-hidden z-10">
              <div className="scan-line" />

              {/* Top nav bar */}
              <div className="bg-[#1a2472]/80 px-4 py-3 flex items-center justify-between border-b border-[#4a5fd4]/20">
                <div className="text-xs font-bold text-[#8fa3f8]">ShopNova</div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-[#6b5ce7]/30 flex items-center justify-center text-xs">🔍</div>
                  <div className="w-6 h-6 rounded-full bg-[#6b5ce7]/30 flex items-center justify-center text-xs relative">
                    🛒
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#6b5ce7] rounded-full text-[8px] flex items-center justify-center">3</span>
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-3">
                {/* Flash sale banner with live countdown */}
                <div className="rounded-2xl bg-gradient-to-br from-[#2d3fb5]/60 to-[#6b5ce7]/40 border border-[#4a5fd4]/30 p-3">
                  <div className="text-[10px] text-[#9aa3c2]">Flash Sale</div>
                  <div className="text-sm font-black text-white">Up to 40% OFF ⚡</div>
                  <div className="text-[9px] text-[#8fa3f8] mt-0.5">
                    Ends in <Countdown />
                  </div>
                </div>

                {/* Category pills */}
                <div className="flex gap-2 overflow-hidden">
                  {["All", "Fashion", "Electronics", "Home"].map((c, i) => (
                    <span
                      key={i}
                      className={`px-2 py-1 rounded-full text-[9px] whitespace-nowrap ${
                        i === 0
                          ? "bg-[#6b5ce7] text-white"
                          : "bg-[#1a2472]/60 text-[#9aa3c2] border border-[#4a5fd4]/20"
                      }`}
                    >
                      {c}
                    </span>
                  ))}
                </div>

                {/* Product grid */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { emoji: "👟", name: "Air Max Pro", price: "$129", badge: "New" },
                    { emoji: "⌚", name: "SmartWatch X", price: "$249", badge: "Hot" },
                    { emoji: "👜", name: "Tote Bag",     price: "$89",  badge: null },
                    { emoji: "🎧", name: "ANC Buds",     price: "$199", badge: "Sale" },
                  ].map((p, i) => (
                    <div
                      key={i}
                      className="rounded-xl bg-[#1a2472]/60 border border-[#4a5fd4]/20 p-2 hover:border-[#6b5ce7]/50 transition duration-200 cursor-pointer"
                    >
                      <div className="h-14 rounded-lg bg-[#2d3fb5]/20 flex items-center justify-center text-2xl relative">
                        {p.emoji}
                        {p.badge && (
                          <span className="absolute top-1 right-1 text-[7px] bg-[#6b5ce7] px-1 rounded-full">{p.badge}</span>
                        )}
                      </div>
                      <div className="text-[9px] font-bold mt-1 truncate">{p.name}</div>
                      <div className="text-[9px] text-[#8fa3f8] font-black">{p.price}</div>
                    </div>
                  ))}
                </div>

                {/* Checkout bar */}
                <div className="rounded-xl bg-gradient-to-r from-[#2d3fb5] to-[#6b5ce7] p-2.5 flex items-center justify-between">
                  <div>
                    <div className="text-[8px] text-[#c5cde8]">Cart Total</div>
                    <div className="text-xs font-black">$577.00</div>
                  </div>
                  <div className="text-[9px] font-bold bg-white text-[#1a2472] px-3 py-1.5 rounded-lg cursor-pointer hover:bg-[#e8eaff] transition">
                    Checkout →
                  </div>
                </div>
              </div>
            </div>

            {/* Floating badge 1 */}
            <div className="badge-pop-1 float-b1 absolute top-6 right-4 bg-[#1a2472]/80 border border-[#4a5fd4]/40 backdrop-blur-xl rounded-2xl px-4 py-3 text-sm z-20 shadow-xl">
              <div className="text-[#9aa3c2] text-xs">Conversion Rate</div>
              <div className="text-lg font-black text-[#8fa3f8]">↑ 3.8%</div>
            </div>

            {/* Floating badge 2 */}
            <div className="badge-pop-2 float-b2 absolute bottom-20 right-0 bg-[#1a2472]/80 border border-[#4a5fd4]/40 backdrop-blur-xl rounded-2xl px-4 py-3 text-sm z-20 shadow-xl">
              <div className="text-[#9aa3c2] text-xs">Revenue</div>
              <div className="text-lg font-black text-[#8fa3f8]">$2B+</div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ SERVICES ════════ */}
      <section className="py-28 px-6 lg:px-20 bg-[#0d1128]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6">
            <div>
              <p className="text-[#6b5ce7] font-semibold tracking-widest text-sm uppercase mb-3">What We Build</p>
              <h2 className="text-5xl font-black leading-tight">
                E-Commerce
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#4a5fd4] to-[#6b5ce7]">Solutions</span>
              </h2>
            </div>
            <p className="text-[#9aa3c2] max-w-md leading-7">
              From simple storefronts to complex multi-vendor marketplaces — we build for conversion, scale, and growth.
            </p>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {[
              { icon:"🛍️", color:"from-[#6b5ce7] to-[#4a5fd4]", title:"Shopify Development", desc:"Custom Shopify themes, apps, and store builds. From launch-ready setups to fully bespoke storefronts with advanced features." },
              { icon:"🔧", color:"from-[#2d3fb5] to-[#4a5fd4]", title:"WooCommerce Stores", desc:"WordPress-based stores with full WooCommerce customization, plugin development, and performance optimization." },
              { icon:"⚡", color:"from-[#4a5fd4] to-[#6b5ce7]", title:"Headless Commerce", desc:"Next.js or Nuxt frontends powered by Shopify, Medusa, or custom APIs — blazing fast, SEO-optimized storefronts." },
              { icon:"🏪", color:"from-[#6b5ce7] to-[#2d3fb5]", title:"Multi-Vendor Marketplace", desc:"Amazon-style marketplaces with vendor dashboards, commission systems, product management, and dispute handling." },
              { icon:"💳", color:"from-[#2d3fb5] to-[#6b5ce7]", title:"Payment & Checkout", desc:"Stripe, PayPal, Razorpay, COD, and Buy Now Pay Later integrations. One-click checkout and cart abandonment recovery." },
              { icon:"📦", color:"from-[#4a5fd4] to-[#2d3fb5]", title:"Inventory & ERP Integration", desc:"Connect your store to Zoho, SAP, Odoo, or custom ERPs. Real-time inventory sync across warehouse and storefront." },
            ].map((s, i) => (
              <div key={i} className="group relative rounded-[28px] overflow-hidden border border-[#4a5fd4]/20 hover:border-[#6b5ce7]/60 transition-all duration-500 hover:-translate-y-2 bg-[#12163a]">
                <div className={`h-1 w-full bg-gradient-to-r ${s.color}`} />
                <div className="p-8">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center text-2xl mb-6`}>{s.icon}</div>
                  <h3 className="text-xl font-bold mb-3">{s.title}</h3>
                  <p className="text-[#9aa3c2] leading-7 text-sm">{s.desc}</p>
                  <div className="mt-6 flex items-center gap-2 text-[#8fa3f8] font-semibold text-sm group-hover:gap-4 transition-all duration-300">Learn More <span>→</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ PROCESS ════════ */}
      <section className="py-28 px-6 lg:px-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#6b5ce7] font-semibold tracking-widest text-sm uppercase mb-3">How We Work</p>
            <h2 className="text-5xl font-black">Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4a5fd4] to-[#6b5ce7]">Process</span></h2>
          </div>
          <div className="relative">
            <div className="absolute left-[28px] top-0 bottom-0 w-px bg-gradient-to-b from-[#6b5ce7] via-[#4a5fd4] to-transparent hidden md:block" />
            <div className="space-y-8">
              {[
                { num:"01", title:"Discovery & Store Strategy", desc:"We audit your business goals, target audience, product catalog, and competitors. You get a detailed store blueprint including platform recommendation, feature list, and conversion strategy." },
                { num:"02", title:"UX Design & Wireframes", desc:"We design your storefront, product pages, cart, and checkout flows in Figma. Optimized for conversion with real UX principles — not just pretty screens." },
                { num:"03", title:"Development & Build", desc:"Agile sprints with weekly demos. Clean, well-structured code whether it's a Shopify theme, custom WooCommerce plugin, or headless React storefront." },
                { num:"04", title:"Integrations & Testing", desc:"Payments, shipping, inventory, CRM, analytics — all wired up and tested. Cross-browser, cross-device QA with real purchase flow testing before launch." },
                { num:"05", title:"Launch & Go-Live", desc:"Zero-downtime migration if you're moving from an existing platform. DNS, SSL, CDN, speed optimization, and SEO all handled before the first sale." },
                { num:"06", title:"Growth & Optimization", desc:"Post-launch A/B testing, conversion rate optimization, analytics dashboards, and feature expansion. We grow with your store." },
              ].map((item, i) => (
                <div key={i} className="relative flex gap-8 items-start group">
                  <div className="w-14 h-14 flex-shrink-0 rounded-full bg-gradient-to-br from-[#2d3fb5] to-[#6b5ce7] flex items-center justify-center font-black text-sm z-10 group-hover:scale-110 transition duration-300">{item.num}</div>
                  <div className="flex-1 p-6 rounded-2xl bg-[#12163a] border border-[#4a5fd4]/20 group-hover:border-[#6b5ce7]/50 transition duration-300 mb-2">
                    <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                    <p className="text-[#9aa3c2] leading-7 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════ TECH STACK ════════ */}
      <section className="py-28 px-6 lg:px-20 bg-[#0d1128]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
          <div>
            <p className="text-[#6b5ce7] font-semibold tracking-widest text-sm uppercase mb-3">Technologies</p>
            <h2 className="text-5xl font-black leading-tight">Cutting-Edge <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#4a5fd4] to-[#6b5ce7]">Tech Stack</span></h2>
            <p className="text-[#9aa3c2] mt-6 leading-8 text-lg">We choose the best platform for your business — not the easiest one for us. Every tech decision is backed by data and experience.</p>
            <div className="mt-10 space-y-4">
              {[
                { label:"Shopify & Shopify Plus", pct:97 },
                { label:"Next.js / Headless Commerce", pct:92 },
                { label:"WooCommerce & WordPress", pct:90 },
                { label:"Magento / Adobe Commerce", pct:82 },
              ].map((bar, i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-white font-medium">{bar.label}</span>
                    <span className="text-[#8fa3f8]">{bar.pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#2d3fb5] to-[#6b5ce7]" style={{width:`${bar.pct}%`}} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label:"Shopify",    sub:"Platform" }, { label:"Next.js",    sub:"Frontend" },   { label:"WooCommerce", sub:"WordPress" },
              { label:"Stripe",     sub:"Payments" }, { label:"Algolia",    sub:"Search" },      { label:"Klaviyo",     sub:"Email/SMS" },
              { label:"Contentful", sub:"CMS" },      { label:"AWS",        sub:"Cloud" },        { label:"Vercel",      sub:"Hosting" },
            ].map((t, i) => (
              <div key={i} className="p-4 rounded-2xl bg-[#12163a] border border-[#4a5fd4]/20 hover:border-[#6b5ce7]/60 hover:-translate-y-1 transition duration-300 text-center">
                <div className="font-bold text-sm">{t.label}</div>
                <div className="text-[#9aa3c2] text-xs mt-1">{t.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ INDUSTRIES ════════ */}
      <section className="py-28 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#6b5ce7] font-semibold tracking-widest text-sm uppercase mb-3">Store Types</p>
            <h2 className="text-5xl font-black">Stores for Every <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4a5fd4] to-[#6b5ce7]">Niche</span></h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { icon:"👗", label:"Fashion & Apparel",  desc:"Size guides, lookbooks, wishlists" },
              { icon:"💄", label:"Beauty & Cosmetics",  desc:"Shade finders, bundles, subscriptions" },
              { icon:"🖥️", label:"Electronics",         desc:"Compare specs, warranties, upsells" },
              { icon:"🏠", label:"Home & Furniture",    desc:"AR previews, room builders" },
              { icon:"🌿", label:"Health & Wellness",   desc:"Subscriptions, dosage tools" },
              { icon:"📚", label:"Digital Products",    desc:"Downloads, courses, SaaS" },
              { icon:"🐾", label:"Pet & Lifestyle",     desc:"Breed filters, auto-refill" },
              { icon:"🏋️", label:"Sports & Fitness",   desc:"Gear bundles, fit guides" },
            ].map((ind, i) => (
              <div key={i} className="group p-6 rounded-[24px] bg-[#12163a] border border-[#4a5fd4]/20 hover:border-[#6b5ce7]/60 hover:-translate-y-2 transition duration-300">
                <div className="text-3xl mb-3">{ind.icon}</div>
                <div className="font-bold text-sm">{ind.label}</div>
                <div className="text-[#9aa3c2] text-xs mt-1 leading-5">{ind.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ CTA ════════ */}
      

    </div>
  );
}